class PolicyPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.body = this.querySelector('[data-policy-body]');
    this.toc = this.querySelector('[data-policy-toc]');
    this.overviewLabel = this.dataset.overviewLabel || 'Overview';
    this.pageTitle = (this.body && this.body.dataset.policyTitle) || '';
    this.sections = [];
    this.observer = null;

    this.buildToc();
    this.bindTocDisclosure();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  slugify(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 72);
  }

  uniqueId(base) {
    let id = base || 'section';
    let n = 2;
    while (document.getElementById(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    return id;
  }

  headingLabel(node) {
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  titlesMatch(a, b) {
    return this.slugify(a) === this.slugify(b);
  }

  leadingUppercaseLabel(text) {
    const match = String(text || '').trim().match(
      /^([A-Z0-9][A-Z0-9 .,&/'’-]{2,}?)(?=\s+[A-Z][a-z]|\s+[a-z])/
    );
    if (!match) return '';
    const label = match[1].replace(/\s+/g, ' ').trim();
    if (label.length < 4 || label.length > 60) return '';
    return label;
  }

  toTitleCase(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\b[a-z]/g, (char) => char.toUpperCase());
  }

  firstMeaningfulChild(node) {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.COMMENT_NODE) continue;
      if (child.nodeType === Node.TEXT_NODE && !child.textContent.trim()) continue;
      if (child.nodeType === Node.ELEMENT_NODE && child.tagName === 'BR') continue;
      return child.nodeType === Node.ELEMENT_NODE ? child : null;
    }
    return null;
  }

  isHeadingStrong(strong) {
    if (!strong || strong.closest('li, a, h1, h2, h3, h4, button')) return false;
    const text = this.headingLabel(strong);
    if (!text || text.length > 70 || text.split(/\s+/).length > 8) return false;
    if (/[.!?]$/.test(text)) return false;

    const parent = strong.parentElement;
    if (!parent) return false;

    const headingNode = parent.tagName === 'SPAN' ? parent : strong;
    const isAllCaps = text === text.toUpperCase();
    const underlined =
      parent.tagName === 'SPAN' &&
      /underline/i.test(`${parent.getAttribute('style') || ''} ${parent.className || ''}`);

    if (underlined && isAllCaps) return true;

    const paragraph = strong.closest('p');
    if (!paragraph || this.firstMeaningfulChild(paragraph) !== headingNode) return false;
    if (isAllCaps) return true;

    let next = headingNode.nextSibling;
    while (next && next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) {
      next = next.nextSibling;
    }
    return Boolean(next && next.nodeType === Node.ELEMENT_NODE && next.tagName === 'BR');
  }

  collectSections() {
    if (!this.body) return [];

    const collected = [];
    const headings = Array.from(this.body.querySelectorAll('h1, h2, h3'));

    headings.forEach((heading) => {
      const label = this.headingLabel(heading);
      if (!label) return;
      if (this.pageTitle && this.titlesMatch(label, this.pageTitle)) {
        heading.setAttribute('hidden', '');
        return;
      }
      collected.push({ node: heading, label, level: heading.tagName });
    });

    Array.from(this.body.querySelectorAll('strong, b')).forEach((strong) => {
      if (!this.isHeadingStrong(strong)) return;
      const raw = this.headingLabel(strong);
      if (this.pageTitle && this.titlesMatch(raw, this.pageTitle)) {
        const wrap = strong.closest('p');
        if (wrap && this.headingLabel(wrap) === raw) wrap.setAttribute('hidden', '');
        return;
      }
      if (/on shopify/i.test(raw)) {
        const wrap = strong.closest('p');
        if (wrap) wrap.setAttribute('hidden', '');
        return;
      }
      const label = raw === raw.toUpperCase() ? this.toTitleCase(raw) : raw;
      const node = strong.closest('span') || strong;
      node.classList.add('policy-page__inline-heading');
      collected.push({ node, label, level: 'STRONG' });
    });

    const firstVisible = Array.from(this.body.children).find(
      (el) => !el.hasAttribute('hidden')
    );
    const firstHeading = collected[0] && collected[0].node;
    const headingIsFirst = Boolean(firstHeading && firstHeading === firstVisible);

    if (!headingIsFirst) {
      const sentinel = document.createElement('span');
      sentinel.className = 'policy-page__overview-anchor';
      sentinel.setAttribute('aria-hidden', 'true');
      this.body.insertBefore(sentinel, this.body.firstChild);
      collected.unshift({
        node: sentinel,
        label: this.overviewLabel,
        level: 'OVERVIEW',
      });
    }

    const used = new Set();
    return collected.filter((section) => {
      const key = this.slugify(section.label);
      if (!key || used.has(key)) return false;
      used.add(key);
      return true;
    });
  }

  buildToc() {
    if (!this.body || !this.toc) return;

    this.sections = this.collectSections();
    this.toc.replaceChildren();

    this.sections.forEach((section) => {
      if (!section.node.id) {
        section.node.id = this.uniqueId(this.slugify(section.label) || 'section');
      }

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'policy-page__toc-link';
      link.href = `#${section.node.id}`;
      link.textContent = section.label;
      link.addEventListener('click', (event) => {
        const target = document.getElementById(section.node.id);
        if (!target) return;
        event.preventDefault();
        this.closeTocIfMobile();
        requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        history.replaceState(null, '', `#${section.node.id}`);
      });
      item.appendChild(link);
      this.toc.appendChild(item);
      section.link = link;
    });

    this.observeSections();
  }

  observeSections() {
    if (this.observer) this.observer.disconnect();
    if (!this.sections.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (!visible.length) return;
        this.setActive(visible[0].target.id);
      },
      {
        rootMargin: '-20% 0px -65% 0px',
        threshold: [0, 0.1, 0.25, 0.5],
      }
    );

    this.sections.forEach((section) => this.observer.observe(section.node));
    if (this.sections[0]) this.setActive(this.sections[0].node.id);
  }

  setActive(id) {
    this.sections.forEach((section) => {
      section.link.classList.toggle('is-active', section.node.id === id);
    });
  }

  isDesktopToc() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  closeTocIfMobile() {
    if (!this.tocDetails || this.isDesktopToc()) return;
    this.tocDetails.open = false;
  }

  bindTocDisclosure() {
    this.tocDetails = this.querySelector('[data-policy-toc-details]');
    if (!this.tocDetails) return;

    const sync = () => {
      this.tocDetails.open = this.isDesktopToc();
    };

    sync();
    window.matchMedia('(min-width: 990px)').addEventListener('change', sync);
  }
}

if (!customElements.get('policy-page')) {
  customElements.define('policy-page', PolicyPage);
}
